import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  CompanyProductAssociationUpdateInput,
  UpdateCompanyProductAssociationMutation,
  UpdateCompanyProductAssociationMutationVariables,
} from "@/graphql/codegen/graphql"
import { UpdateCompanyProductAssociationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateCompanyProductAssociationReturn {
  updateAssociation: (input: CompanyProductAssociationUpdateInput) => Promise<boolean>
  isUpdatingAssociation: boolean
}

export const useUpdateCompanyProductAssociation = (): UseUpdateCompanyProductAssociationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    UpdateCompanyProductAssociationMutation,
    UpdateCompanyProductAssociationMutationVariables
  >(UpdateCompanyProductAssociationDocument)

  const updateAssociation = useCallback(
    async (input: CompanyProductAssociationUpdateInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.companyProductAssociation.update

      const hasError = error || output?.__typename === "CompanyProductAssociationUpdateFailure"

      if (hasError) {
        const failureMessage =
          output?.__typename === "CompanyProductAssociationUpdateFailure" ? output.error.message : error?.message

        notify({
          type: NotificationType.Error,
          silent: false,
          message: failureMessage ?? "Failed to update association. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        message: "Association updated",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { updateAssociation, isUpdatingAssociation: fetching }
}
